/**
 * Every MoEngage event name, exactly as in the business sheet
 * ("Prospect - Events list as on Sep 2026 - MoEngage.xlsx", Events_Master).
 *
 * Do NOT "fix" the spelling: double underscores (`signature_loaded__en`) and
 * mixed case (`Pan_Verified_CDP`) are intentional — they match the names the
 * existing CleverTap / GTM pushes already use, and MoEngage names are
 * case-sensitive. These are the LEGACY names; what MoEngage receives is in
 * moengageJourneyMap.js (docs/moengage_events_handover.md).
 */

const deepFreeze = (obj) => {
    Object.values(obj).forEach((v) => {
        if (v && typeof v === 'object') deepFreeze(v);
    });
    return Object.freeze(obj);
};

export const EVENTS = deepFreeze({
    REGISTRATION: {
        PAGE_LOADED: 'registration_page_loaded_en',
        PROCEED: 'registration_page_proceed_en',
        LEAD_FILLED_CDP: 'lead_filled_cdp',
    },
    OTP: {
        VERIFIED_SUCCESS: 'otp_verified_success_en',
        LEAD_VERIFIED: 'lead_verified_sa',
        LEAD_VERIFIED_CDP: 'lead_verified_cdp',
    },
    EMAIL: {
        GOOGLE_SIGNIN_INITIATED: 'email_google_signin_initiated_en',
        GOOGLE_SIGNIN_SUCCESS: 'email_google_signin_success_en',
        MANUALLY_SUBMITTED: 'email_manually_submitted_en',
        OTP_VERIFIED_SUCCESS: 'email_otp_verified_success_en',
    },
    PAN: {
        PAGE_LOADED: 'panpage_loaded_en',
        PROCEED: 'panpage_proceed_en',
        VERIFICATION_COMPLETED: 'pan_verification_completed',
    },
    PAN_VERIFIED: {
        PAGE_LOADED: 'panverifiedpage_loaded_en',
        PROCEED: 'panverifiedpage_proceed_en',
        CDP: 'Pan_Verified_CDP',
    },
    DIGILOCKER: {
        INITIATED: 'digilocker_initiated_en',
        SUCCESS: 'digilocker_success_en',
    },
    BANK: {
        PENNYDROP_INITIATED: 'bank_pennydrop_initiated_en',
        PENNYDROP_SUCCESS: 'bank_pennydrop_success_en',
        RPD_INITIATED: 'bank_rpd_initiated_en',
        RPD_SUCCESS: 'bank_rpd_success_en',
        VERIFICATION_COMPLETED: 'bank_verification_completed',
    },
    SELFIE: {
        BANK_VERIFIED_CDP: 'Bank_Verified_CDP',
        SCREEN_LOADED: 'selfie_screen_loaded_en',
        SCREEN_PROCEED: 'selfie_screen_proceed_en',
        LOCATION_PERMISSION: 'location_permission_en',
        LOCATION_DENIED: 'location_denied_en',
    },
    SIGNATURE: {
        DOCUMENT_UPLOAD_COMPLETED: 'document_upload_completed_sa',
        LOADED: 'signature_loaded__en',
        PROCEED: 'signature_proceed__en',
    },
    OTHER_DETAILS: {
        LOADED: 'odetails_loaded__en',
        // Replaces odetails_Verified__en for MoEngage: the app sends odetails_proceed__en (handover §3).
        PROCEED: 'odetails_proceed__en',
        INCOME_PROOF_INITIATED: 'income_proof_initiated_en',
        INCOME_PROOF_SUCCESS: 'income_proof_success_en',
        INCOME_PROOF_VIEWED: 'income_proof_viewed_en',
        INCOME_PROOF_NOT_VISITED: 'income_proof_not_visited_en',
        NOMINEE_ADDED: 'nominee_added',
        INCOME_ACCOUNT_AGGREGATOR: 'income_account_aggregator',
        FNO_SELECTED: 'fno_selected',
    },
    AOF: {
        GENERATED_SUCCESS: 'aof_generated_success_en',
    },
    ESIGN: {
        COMPLETED_SUCCESS: 'esign_completed_success_en',
        DONE: 'esign_done_sa',
        COMPLETED_FAILED: 'esign_completed_failed_en',
    },
    ACCOUNT_CREATION: {
        SUCCESS: 'account_creation_success_en',
        ADD_FUNDS: 'add_funds',
        // Sheet name is "funds_added- (Success)" — not a valid clean name. Pending confirmation.
        FUNDS_ADDED: 'funds_added',
    },
    ACCOUNT_UNDER_VERIFICATION: {
        LOADED: 'account_under_verification_en',
    },
    // Rejection review page (/dad/rejectionCorrection).
    REJECTION: {
        PAGE_LOADED: 'rejection_page_loaded_en',
        PAGE_VIEWED: 'rejection_page_viewed_en',
        PROCEED: 'rejection_proceed_en',
        FSN_OPENED: 'rejection_fsn_opened_en',
        FSN_SUBMITTED: 'rejection_fsn_submitted_en',
        FSN_ERROR: 'rejection_fsn_error_en',
        DIGILOCKER_REDO_INITIATED: 'digilocker_redo_initiated_en',
        SELFIE_REDO_INITIATED: 'selfie_redo_initiated_en',
    },
    // From the Event_Properties sheet. ekyc_journey_dropped_en is defined but not sent:
    // "> 30 min idle" can only be decided server-side.
    JOURNEY: {
        WEB_SESSION_INIT: 'web_session_init_en',
        STARTED: 'ekyc_journey_started_en',
        RESUMED: 'ekyc_journey_resumed_en',
        DROPPED: 'ekyc_journey_dropped_en',
        COMPLETED: 'ekyc_journey_completed_en',
    },
});

/** Flat list of every event name. */
export const ALL_EVENT_NAMES = Object.freeze(
    Object.values(EVENTS).reduce((acc, group) => acc.concat(Object.values(group)), [])
);
